import { useRef } from "react"
import parse from "html-react-parser"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useNavigation } from "contexts/Navigation"
import { useDictionary } from "contexts/Dictionary"

import { body100, body250, bold, legend100, surtitle45, surtitle50 } from "css/text"
import { contrastMode } from "css/a11y"
import { buttonReset } from "css/buttons"

import Image from "components/media/ImageCLD"
import VideoCLD, { cssContainer, cssVideo } from "components/media/VideoCLD"
import VideoPlayerCLD from "components/media/VideoPlayerCLD"
import { buildHref } from "components/scrolling-grid-aggregator/ScrollingGrid"
import getMediaQuery from "css/breakpoints"
import { useLocale } from "contexts/Locale"

const A = styled.a`
  display: grid;
  position: relative;
  width: 100%;
  height: 100%;
  text-decoration: none;
  overflow: hidden;

  & * {
    pointer-events: none;
  }

  color: rgb(var(--light-black));

  &.dark-theme {
    color: rgb(var(--pure-white));
  }

  &:focus img {
    transform: scale(1.05) translate3d(0, 0, 0);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover img {
      transform: scale(1.05) translate3d(0, 0, 0);
    }
  }
`

const ButtonPlayer = styled.button`
  ${buttonReset}
  display: grid;
  position: relative;
  width: 100%;
  text-align: start;

  color: rgb(var(--light-black));

  &.dark-theme {
    color: rgb(var(--pure-white));
  }
`

const Figure = styled.figure`
  width: 100%;

  display: grid;
  & picture {
    display: block;
    grid-column: 1 / -1;
    grid-row: 1;
    overflow: hidden;
  }

  & .${cssContainer} {
    grid-column: 1 / -1;
    grid-row: 1;

    display: block;
    width: 100%;
    height: 100%;
  }

  & .${cssVideo} {
    height: 100%;
  }

  & img {
    grid-column: 1 / -1;
    grid-row: 1;

    display: block;
    width: 100%;
    height: auto;
    transform: scale(1) translate3d(0, 0, 0);
    transition: transform 0.4s ease-out;
  }

  &::after {
    content: "";
    display: block;
    position: absolute;
    inset-inline: 0;
    inset-block-end: 0;
    height: 60%;
    background: linear-gradient(rgba(0 0 0 / 0), rgba(0 0 0 / 0.3));
  }
`

const Container = styled.figcaption`
  display: grid;
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  grid-template-columns: 30px auto 25%;
  grid-template-rows: 30px auto min-content 30px;
`

const Hgroup = styled.hgroup`
  grid-column: 2;
  grid-row: 2;
  z-index: 1;
  width: 100%;
  align-self: end;
  position: relative;
  display: grid;
  grid-template-rows: repeat(2, min-content);

  height: fit-content;

  & > h3 {
    grid-row-start: 2;
    grid-column: 1 / -1;

    margin-block-end: 15px;
    width: 100%;
  }

  & > p {
    grid-row-start: 1;
  }
  ${contrastMode}
`

const Kicker = styled.p`
  ${surtitle45}
`

const CTA = styled.span`
  ${legend100}
  ${bold}
  font-size: 1rem;
  position: relative;
  grid-column: 2;
  grid-row: 3;
  z-index: 1;
  align-self: end;
  justify-self: start;
  line-height: 1;
  ${contrastMode}
  & svg {
    margin-inline: 0.5rem;
    margin-bottom: -1px;
    width: 14px;
    height: 14px;
  }
  ${getMediaQuery("m")} {
    ${legend100}
  }
`

const SEDate = styled.p`
  ${surtitle50}
  margin-block-end: 5px;
`

const Title = styled.h3`
  ${body250}
  ${bold}
  line-height: 1.2;
  position: relative;
  grid-column: 2;
  grid-row: 2;
  z-index: 1;
  margin-block-end: 15px;
  width: 100%;
  align-self: end;
  &.onlytitle {
    ${contrastMode}
  }
  ${getMediaQuery("m")} {
    ${body100}
  }
`

function buildAsset(o) {
  const {
    public_id_image_0,
    public_id_video_0,
    public_image_0_width,
    public_image_0_height,
    public_id_image_1,
    public_id_video_1,
    public_image_1_width,
    public_image_1_height,
    public_id_image_2,
    public_id_video_2,
    public_image_2_width,
    public_image_2_height,
  } = o

  return [
    {
      resource_type: "image",
      public_id: public_id_image_0,
      width: public_image_0_width,
      height: public_image_0_height,
    },
    {
      resource_type: "image",
      public_id: public_id_image_1,
      width: public_image_1_width,
      height: public_image_1_height,
    },
    {
      resource_type: "image",
      public_id: public_id_image_2,
      width: public_image_2_width,
      height: public_image_2_height,
    },
    {
      resource_type: "video",
      public_id: public_id_video_0,
      width: public_image_0_width,
      height: public_image_0_height,
    },
    {
      resource_type: "video",
      public_id: public_id_video_1,
      width: public_image_1_width,
      height: public_image_1_height,
    },
    {
      resource_type: "video",
      public_id: public_id_video_2,
      width: public_image_2_width,
      height: public_image_2_height,
    },
  ].filter(v => v.public_id)
}

function Media({ media }) {
  const console = useConsole()
  //  console.verbose("MediaMedia", media)
  const isVideo = media.filter(a => a.resource_type === "video").length

  return !isVideo ? <Image sources={media} sizes='(min-width: 48rem) 35vw, 70vw' alt='' /> : <VideoCLD sources={media} noButton={true} />
}

function LinkAction({ index, href, theme, cardName, children }) {
  const console = useConsole()
  const navigation = useNavigation()
  const linkurl = navigation.localize(href ?? "#")

  function track() {
    global.sessionStorage.setItem(
      "__ddlAddOn",
      JSON.stringify([
        "rollerRefClicked",
        {
          rollerRefInfo: {
            cardName: cardName || "",
            cardPosition: index + 1,
          },
        },
      ])
    )
  }

  function navigate(e) {
    e.preventDefault()
    track()
    navigation.navigate(e.target.getAttribute("href"))
  }

  return (
    <A className={theme ?? ""} href={linkurl} onClick={navigate} data-index={index}>
      {children}
    </A>
  )
}

export default function DynaCard({ index, ...props }) {
  const console = useConsole()
  const dictionary = useDictionary()
  const { locale } = useLocale()

  const { baseline, name, parent_element_uid, start_date, end_date } = props

  let ass = buildAsset(props).filter(v => +v.width >= +v.height)

  function formatDate(tm) {
    return new Intl.DateTimeFormat(locale.codes.ws, {
      month: "long",
      day: "2-digit",
    }).format(tm)
  }

  const dateSub =
    start_date || end_date ? `${start_date ? `${formatDate(start_date)}${end_date ? " – " : ""}${end_date ? formatDate(end_date) : ""}` : ""}` : null

  ass = ass
    .filter(v => v.metadata)
    .slice(0, 1)
    .concat(ass.filter(v => !v.metadata).slice(0, 1))

  return (
    <LinkAction theme='dark-theme' index={index} href={buildHref(props)} cardName={name}>
      <Figure>
        <Media media={ass} />
        <Container>
          {name && baseline ? (
            <Hgroup>
              {dateSub ? <SEDate>{dateSub}</SEDate> : null}
              {baseline && parent_element_uid ? <Kicker>{baseline}</Kicker> : null}
              {name && <Title>{parse(name)}</Title>}
            </Hgroup>
          ) : name && dateSub ? (
            <Hgroup>
              {dateSub ? <SEDate>{dateSub}</SEDate> : null}
              <Title>{parse(name)}</Title>
            </Hgroup>
          ) : name ? (
            <Title className='onlytitle'>{parse(name)}</Title>
          ) : null}
          <CTA>{dictionary.discoverMore()}</CTA>
        </Container>
      </Figure>
    </LinkAction>
  )
}
